package kz.dentalcrm.app.screens.patients.add

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.BottomSheetDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.Text
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import kz.dentalcrm.app.screens.patients.add.mvi.AddPatientViewEvent
import kz.dentalcrm.app.screens.patients.add.mvi.AddPatientViewSideEffect
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import co.touchlab.kermit.Logger
import compose.icons.FeatherIcons
import compose.icons.feathericons.Phone
import compose.icons.feathericons.User
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.add_patient
import dentalcrm.composeapp.generated.resources.add_patient_title
import dentalcrm.composeapp.generated.resources.name
import dentalcrm.composeapp.generated.resources.phone
import dentalcrm.composeapp.generated.resources.surname
import kz.dentalcrm.app.core.ui.UiStateScaffold
import kz.dentalcrm.app.core.ui.components.UIButton
import kz.dentalcrm.app.core.ui.components.UITextInput
import kz.dentalcrm.app.core.ui.utils.MaskVisualTransformation
import kz.dentalcrm.app.data.network.response_model.PatientData
import kz.dentalcrm.app.expectations.rememberContactPickerState
import kz.dentalcrm.app.theme.DefaultTheme
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

@OptIn(ExperimentalMaterial3Api::class, ExperimentalUuidApi::class)
@Composable
fun AddPatientDialog(
    onAdd: (PatientData?) -> Unit,
    onDismiss: () -> Unit,
) {
    var key by rememberSaveable { mutableStateOf(Uuid.random().toHexString()) }
    val modalBottomSheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)
    val screenModel = koinViewModel<AddPatientModel>(key = key)
    val state by screenModel.viewState.collectAsState()

    LaunchedEffect(screenModel.effect) {
        screenModel.effect.collect { effect ->
            when (effect) {
                is AddPatientViewSideEffect.PatientAdded -> onAdd(effect.patient)
            }
        }
    }

    ModalBottomSheet(
        modifier = Modifier.imePadding().statusBarsPadding(),
        onDismissRequest = onDismiss,
        sheetState = modalBottomSheetState,
        dragHandle = { BottomSheetDefaults.DragHandle() },
        containerColor = DefaultTheme.colors.surfaceSecondary,
        content = {
            UiStateScaffold(
                modifier = Modifier.background(Color.Transparent),
                content = {
                    AddPatientContent(
                        loading = state.isLoading,
                        addPatient = { name, surname, phone ->
                            screenModel.setEvent(AddPatientViewEvent.AddPatient(name, surname, phone))
                        }
                    )
                }
            )
        }
    )
}

@Composable
private fun AddPatientContent(
    loading: Boolean = false,
    addPatient: (String, String, String) -> Unit,
) {
    Column(
        modifier = Modifier
            .background(
                color = DefaultTheme.colors.surfaceSecondary,
                shape = RoundedCornerShape(topStart = 16.dp, topEnd = 16.dp)
            )
            .verticalScroll(rememberScrollState())
            .fillMaxWidth(),
    ) {
        var name by remember { mutableStateOf("") }
        var surname by remember { mutableStateOf("") }
        var phone by remember { mutableStateOf("") }

        val contactPicker = rememberContactPickerState {
            Logger.i {
                it?.phoneNumbers.toString()
            }
        }

        val contactSelected by contactPicker.value

        LaunchedEffect(contactSelected) {
            name = contactSelected?.name?.let { name ->
                if (name.contains(" ")) {
                    name.split(" ").firstOrNull().orEmpty()
                } else {
                    name
                }
            } ?: ""

            surname = contactSelected?.name?.let { name ->
                if (name.contains(" ")) {
                    runCatching {
                        name.split(" ")[1]
                    }.getOrNull() ?: ""
                } else {
                    name
                }
            } ?: ""

            phone = contactSelected?.phoneNumbers?.first().orEmpty()
        }

        Text(
            modifier = Modifier.padding(horizontal = 16.dp),
            text = stringResource(Res.string.add_patient_title),
            style = DefaultTheme.typography.bodyLargeMedium,
            color = DefaultTheme.colors.onSurface
        )
        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))
        HorizontalDivider(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
            thickness = 1.dp,
            color = DefaultTheme.colors.onSurfaceVariant.copy(
                alpha = 0.2f
            )
        )
        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))

        UITextInput(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .fillMaxWidth(),
            leadingIcon = FeatherIcons.User,
            label = stringResource(Res.string.name),
            defaultValue =
                contactSelected?.name?.let { name ->
                    if (name.contains(" ")) {
                        name.split(" ").firstOrNull().orEmpty()
                    } else {
                        name
                    }
                } ?: ""

        ) {
            name = it
        }

        Spacer(modifier = Modifier.fillMaxWidth().height(8.dp))
        UITextInput(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .fillMaxWidth(),
            leadingIcon = FeatherIcons.User,
            label = stringResource(Res.string.surname),
            defaultValue = contactSelected?.name?.let { name ->
                if (name.contains(" ")) {
                    runCatching {
                        name.split(" ")[1]
                    }.getOrNull() ?: ""
                } else {
                    name
                }
            } ?: ""
        ) {
            surname = it
        }

        Spacer(modifier = Modifier.fillMaxWidth().height(8.dp))
        UITextInput(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .fillMaxWidth(),
            leadingIcon = FeatherIcons.Phone,
            maxLength = 10,
            isOnlyNumbers = true,
            keyboardType = KeyboardType.Phone,
            visualTransformation = MaskVisualTransformation("+7 ###-###-####"),
            label = stringResource(Res.string.phone),
            trailingIcon = FeatherIcons.User,
            trailingActionClear = false,
            defaultValue = contactSelected?.phoneNumbers?.first().orEmpty(),
            trailingIconClicked = {
                contactPicker.launchContactPicker()
            }
        ) {
            phone = it
        }

        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))
        UIButton(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .fillMaxWidth(),
            enabled = name.isNotEmpty().and(surname.isNotEmpty()).and(phone.isNotEmpty()),
            text = stringResource(Res.string.add_patient),
            isLoading = loading,
            onClick = {
                addPatient(name, surname, "7$phone")
            },
        )

        Spacer(
            modifier = Modifier.fillMaxWidth().height(32.dp)
                .navigationBarsPadding()
        )
    }
}